import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';
import { apiClient } from '../../utils/apiClient';
import './AddTokenModal.css';

const AddTokenModal = ({ isOpen, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  const { isDark } = useTheme();
  
  const [tokenAddress, setTokenAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [tokenDetails, setTokenDetails] = useState(null);
  const [error, setError] = useState(null);

  const handleClose = () => {
    if (!isSubmitting) {
      setTokenAddress('');
      setNotes('');
      setTokenDetails(null);
      setError(null);
      onClose();
    }
  };

  const validateSolanaAddress = (address) => {
    if (!address || address.length < 32 || address.length > 44) {
      return false;
    }
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    return base58Regex.test(address);
  };

  const fetchTokenDetails = async (address) => {
    if (!validateSolanaAddress(address)) {
      setError('Invalid Solana token address format');
      setTokenDetails(null);
      return;
    }

    setIsFetchingDetails(true);
    setError(null);

    try {
      // Try to fetch token details from DexScreener API
      const response = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${address}`);
      const data = await response.json();

      if (data.pairs && data.pairs.length > 0) {
        const pair = data.pairs[0];
        const tokenInfo = {
          address: address,
          symbol: pair.baseToken?.symbol || 'UNKNOWN',
          name: pair.baseToken?.name || 'Unknown Token',
          price: parseFloat(pair.priceUsd) || 0,
          priceChange24h: parseFloat(pair.priceChange?.h24) || 0,
          volume24h: parseFloat(pair.volume?.h24) || 0,
          marketCap: parseFloat(pair.marketCap) || 0,
          liquidity: parseFloat(pair.liquidity?.usd) || 0,
          imageUrl: pair.baseToken?.image || null,
          description: null,
          website: null,
          twitter: null,
          telegram: null
        };
        setTokenDetails(tokenInfo);
        setError(null);
      } else {
        setError('Token not found on DexScreener. You can still submit it for approval.');
        setTokenDetails({
          address: address,
          symbol: 'UNKNOWN',
          name: 'Unknown Token',
          price: 0,
          priceChange24h: 0,
          volume24h: 0,
          marketCap: 0,
          liquidity: 0,
          imageUrl: null,
          description: null,
          website: null,
          twitter: null,
          telegram: null
        });
      }
    } catch (error) {
      console.warn('Could not fetch token details:', error);
      setError('Could not fetch token details automatically. You can still submit it for approval.');
      setTokenDetails({
        address: address,
        symbol: 'UNKNOWN',
        name: 'Unknown Token',
        price: 0,
        priceChange24h: 0,
        volume24h: 0,
        marketCap: 0,
        liquidity: 0,
        imageUrl: null,
        description: null,
        website: null,
        twitter: null,
        telegram: null
      });
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleAddressChange = (e) => {
    const address = e.target.value.trim();
    setTokenAddress(address);
    
    if (address.length >= 32) {
      // Debounce the API call
      const timeoutId = setTimeout(() => {
        fetchTokenDetails(address);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    } else {
      setTokenDetails(null);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      setError('You must be logged in to submit tokens');
      return;
    }
    
    if (!tokenAddress.trim()) {
      setError('Token address is required');
      return;
    }

    if (!validateSolanaAddress(tokenAddress)) {
      setError('Invalid Solana token address format');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiClient.post('/tokens/submit', {
        tokenAddress: tokenAddress.trim(),
        notes: notes.trim() || null
      });

      if (response.success) {
        showSuccess('Token submitted for admin approval! You will be notified when it\'s reviewed.');
        handleClose();
      } else {
        setError(response.message || 'Failed to submit token');
      }
    } catch (error) {
      console.error('Error submitting token:', error);
      setError(error.message || 'Failed to submit token for approval');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className={`add-token-modal ${isDark ? 'dark' : 'light'}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Token for Approval</h2>
          <button className="modal-close" onClick={handleClose} disabled={isSubmitting}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <p className="modal-description">
            Submit a token by its contract address for admin approval. Once approved, it will appear in the trending tokens list.
          </p>

          {!isAuthenticated && (
            <div className="auth-warning">
              <span className="warning-icon">⚠️</span>
              <span>You must be logged in to submit tokens for approval.</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="tokenAddress">Token Contract Address *</label>
              <input
                id="tokenAddress"
                type="text"
                value={tokenAddress}
                onChange={handleAddressChange}
                placeholder="Enter Solana token mint address (e.g., EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v)"
                disabled={isSubmitting}
                required
              />
              <div className="field-help">
                <p>Enter the token's mint address. You can find this on DexScreener, Solscan, or other Solana explorers.</p>
              </div>
            </div>

            {isFetchingDetails && (
              <div className="loading-details">
                <div className="loading-spinner"></div>
                <span>Fetching token details...</span>
              </div>
            )}

            {tokenDetails && (
              <div className="token-preview">
                <h3>Token Preview</h3>
                <div className="preview-content">
                  <div className="preview-header">
                    {tokenDetails.imageUrl && (
                      <img 
                        src={tokenDetails.imageUrl} 
                        alt={tokenDetails.name}
                        className="preview-logo"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <div className="preview-info">
                      <h4>{tokenDetails.name}</h4>
                      <span className="preview-symbol">{tokenDetails.symbol}</span>
                    </div>
                  </div>
                  
                  <div className="preview-stats">
                    <div className="preview-stat">
                      <span className="stat-label">Price</span>
                      <span className="stat-value">
                        {tokenDetails.price > 0 ? `$${tokenDetails.price.toFixed(6)}` : 'N/A'}
                      </span>
                    </div>
                    <div className="preview-stat">
                      <span className="stat-label">24h Change</span>
                      <span className={`stat-value ${tokenDetails.priceChange24h >= 0 ? 'positive' : 'negative'}`}>
                        {tokenDetails.priceChange24h >= 0 ? '+' : ''}{tokenDetails.priceChange24h.toFixed(2)}%
                      </span>
                    </div>
                    <div className="preview-stat">
                      <span className="stat-label">Market Cap</span>
                      <span className="stat-value">
                        {tokenDetails.marketCap > 0 ? `$${(tokenDetails.marketCap / 1e6).toFixed(2)}M` : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="notes">Notes (Optional)</label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional information about this token..."
                disabled={isSubmitting}
                rows="3"
              />
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <div className="form-actions">
              <button 
                type="button"
                className="cancel-button"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="submit-button"
                disabled={isSubmitting || !tokenAddress.trim() || !isAuthenticated}
              >
                {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddTokenModal;
